import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AbstractDiffusionSessionService } from '@zero/shared/util-abstract-services';
import { Session, SessionOptions, connect } from 'diffusion';
import { environment } from 'environments/environment';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { AuthService } from '../authentication/auth.service';

@Injectable({
  providedIn: 'root',
})
export class DiffusionSessionService extends AbstractDiffusionSessionService implements OnDestroy {
  private session: Session;
  private diffusionSessionSubject$: ReplaySubject<Session> = new ReplaySubject<Session>(1);
  public diffusionSession$: Observable<Session> = this.diffusionSessionSubject$.asObservable();

  private observablesToUnsubcribe: Subscription[] = [];
  private loggedIn = false;
  private sessionRecreationTrigger$ = new Subject<void>();
  private authTokenSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private ngzone: NgZone,
  ) {
    super();
    this.observablesToUnsubcribe.push(
      // only connect to diffusion if a customer is logged in, not for prospects who are currently in Depoteröffnung phase
      this.authService.isLoggedIn$().subscribe((loggedIn) => {
        if (loggedIn) {
          import('diffusion').then(() => {
            this.ngzone.runOutsideAngular(() => {
              this.#connectToDiffusion();
            });
          });
        } else if (this.loggedIn) {
          import('diffusion').then(() => {
            this.disconnectFromDiffusion();
          });
        }
        this.loggedIn = loggedIn;
      }),
    );

    this.observablesToUnsubcribe.push(
      this.sessionRecreationTrigger$.pipe(delay(10000)).subscribe(() => {
        if (this.loggedIn) {
          import('diffusion').then(() => {
            this.#connectToDiffusion();
          });
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.observablesToUnsubcribe.forEach((elem) => {
      elem.unsubscribe();
    });
  }

  #connectToDiffusion(): void {
    if (this.session?.isConnected()) {
      return;
    }

    // Set the maximum amount of time we'll try and reconnect for to 10 minutes
    const maximumTimeoutDuration = 1000 * 60 * 10;

    // Set the maximum interval between reconnect attempts to 60 seconds
    const maximumAttemptInterval = 1000 * 10;

    // Set an upper limit to the number of times we'll try to reconnect for
    const maximumAttempts = 25;

    // Count the number of reconnection attempts we've made
    let attempts = 0;

    // Create a reconnection strategy that applies an exponential back-off
    // The strategy will be called with two arguments, start & abort. Both
    // of these are functions, which allow the strategy to either start a
    // reconnection attempt, or to abort reconnection (which will close the session)
    // copied from https://github.com/pushtechnology/diffusion-examples/blob/master/js/examples/reconnect.js
    const reconnectionStrategy = (start, abort) => {
      if (attempts > maximumAttempts) {
        abort();
      } else {
        const wait = Math.min(Math.pow(2, attempts++) * 100, maximumAttemptInterval);

        // Wait the specified time period, and then start the reconnection attempt
        setTimeout(start, wait);
      }
    };

    this.authTokenSubscription = this.authService.getAccessToken().subscribe((authToken) => {
      const currentUserName = this.authService.getCurrentUserName();
      const options: SessionOptions = {
        host: environment.push_url,
        port: environment.push_port,
        principal: currentUserName,
        credentials: authToken,
        transports: ['WS', 'XHR', 'WS'],
        reconnect: {
          timeout: maximumTimeoutDuration,
          strategy: reconnectionStrategy,
        },
      };

      const session = connect(options);

      session.then(
        (session: Session) => {
          this.session = session;
          localStorage.setItem('diffusionSession', 'connected');

          this.session.on('close', () => {
            this.diffusionSessionSubject$.next(null);
            this.sessionRecreationTrigger$.next();
          });

          this.session.on('disconnect', (reason) => {
            if (!reason.canReconnect) {
              this.diffusionSessionSubject$.next(null);
              this.sessionRecreationTrigger$.next();
            }
          });

          this.session.on('error', () => {
            // TODO: error handling
          });

          this.session.on('reconnect', () => {
            attempts = 0;
          });

          this.diffusionSessionSubject$.next(this.session);
        },
        () => {
          // reconnect scheint uns unzuverlässig und dauert im Zweifel zu lang
          // deshalb versuchen wir es nicht.
          if (this.session) {
            if (!this.session.isClosed()) {
              this.session.close();
            }
            this.session = null;
          }
          // let consumers know that  there is no session atm
          this.diffusionSessionSubject$.next(null);
          this.sessionRecreationTrigger$.next();
        },
      );
    });
  }

  private disconnectFromDiffusion(): void {
    if (this.authTokenSubscription) {
      this.authTokenSubscription.unsubscribe();
    }

    if (this.session && !this.session.isClosed()) {
      this.session.close();
    }
  }
}
